import { useEffect } from "react";
import { useOrganization, useUser, useAuth } from "@clerk/clerk-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";

/**
 * Hook to ensure Clerk organization is synced with local database
 * This handles cases where users might have Clerk organizations but no local sync
 */
export function useOrganizationSync() {
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  const { isSignedIn } = useAuth();

  // Only make Convex calls when user is properly authenticated
  const shouldMakeConvexCalls = isSignedIn && userLoaded && user;

  const syncClerkOrganization = useMutation(api.organizations.syncClerkOrganization);
  const localOrganization = useQuery(
    api.organizations.getByUser,
    shouldMakeConvexCalls ? {} : "skip"
  );

  useEffect(() => {
    // Only sync if:
    // 1. User is authenticated and loaded
    // 2. Organization is loaded
    // 3. User has a Clerk organization
    // 4. Either no local organization exists OR local org name doesn't match Clerk org name
    if (
      shouldMakeConvexCalls &&
      orgLoaded &&
      organization &&
      (!localOrganization || localOrganization.name !== organization.name)
    ) {
      const syncOrganization = async () => {
        try {
          console.log("Syncing Clerk organization with local database:", organization.name);
          await syncClerkOrganization({
            clerkOrgId: organization.id,
            clerkOrgName: organization.name,
            clerkOrgSlug: organization.slug || undefined,
          });
          console.log("Organization sync completed successfully");
        } catch (error) {
          console.error("Failed to sync organization:", error);
        }
      };

      syncOrganization();
    }
  }, [shouldMakeConvexCalls, orgLoaded, organization, localOrganization, syncClerkOrganization]);

  return {
    isOrganizationSynced:
      localOrganization && organization && localOrganization.name === organization.name,
    clerkOrganization: organization,
    localOrganization,
    isLoading:
      !userLoaded || !orgLoaded || (shouldMakeConvexCalls && localOrganization === undefined),
  };
}
